import React, { Fragment, useCallback, useEffect, useState } from 'react';
import { Col, Container, Row } from 'react-grid-system';
import { useDispatch, useSelector } from 'react-redux';
import styled from 'styled-components';
import Button from '../../../components/Button';
import Card from '../../../components/Card';
import PageError from '../../../components/Error';
import FlashMessage from '../../../components/FlashMessage';
import Checkbox from '../../../components/FormFields/Checkbox';
import Loading from '../../../components/Loading';
import Pagination from '../../../components/Pagination';
import RepositCard from '../../../components/Reposit/RepositCard';
import Search from '../../../components/Search';
import { Table, Tbody, Th, Thead, Tr } from '../../../components/Table';
import { Header2, Header3, P1, P2 } from '../../../components/Typography';
import { createErrorMessageSelector } from '../../../redux/error/error.selector';
import { createLoadingSelector } from '../../../redux/loading/loading.selector';
import {
  CREATE_ORG_GROUP_STORE_KEY,
  UPDATE_ORG_GROUP_STORE_KEY,
  updateOrganizationGroupRequested,
} from '../../../redux/organization-group/organization-group.actions';
import {
  cancelOrganizationGroupManagementSearches,
  DELETE_ORG_GROUP_STORE_KEY,
  goToOrganizationGroupOrganizationsPage,
  goToUnusedOrganizationGroupsPage,
  Page,
  SEARCH_ORG_GROUP_ORGANIZATIONS_STORE_KEY,
  SEARCH_UNUSED_ORG_GROUPS_STORE_KEY,
  searchOrganizationGroupOrganizationsRequested,
  searchUnusedOrganizationGroupsRequested,
  updateOrganizationGroupOrganizationFilters,
} from '../../../redux/organization-group/organization-group-management.actions';
import {
  getOrganizationGroupManagement,
  getOrganizationGroupManagementFilters,
  getOrganizationGroupManagementPagination,
  getUnusedOrganizationGroups,
} from '../../../redux/organization-group/organization-group-management.selectors';
import { FLASH_MESSAGE_TIMEOUT, useFlashMessage } from '../../FlashMessage';
import OrganizationGroupEditor, { OrganizationGroupChanges } from '../../OrganizationGroup/OrganizationGroupEditor';
import UnusedOrganizationGroupRow from './UnusedOrganizationGroupRow';

type View = 'organizations' | 'unused';

const ModeStrip = styled.div`
  align-items: center;
  background: ${(props) => props.theme.colors.bgAccent};
  border: 1px solid ${(props) => props.theme.colors.dividerLight};
  border-radius: 12px;
  display: flex;
  flex-wrap: wrap;
  gap: 12px;
  justify-content: space-between;
  margin: 8px 0 32px;
  padding: 12px 16px;
`;

const ViewStrip = styled(ModeStrip)`
  margin-top: 24px;
`;

const ModeActions = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
`;

const Results = styled.div`
  margin-top: 24px;
`;

const FlashMessageContainer = styled.div`
  margin-bottom: 16px;
`;

const OrganizationGroups: React.FC = () => {
  const dispatch = useDispatch();
  const management = useSelector(getOrganizationGroupManagement);
  const managementFilters = useSelector(getOrganizationGroupManagementFilters);
  const managementPagination = useSelector(getOrganizationGroupManagementPagination);
  const unused = useSelector(getUnusedOrganizationGroups);
  const [activeView, setActiveView] = useState<View>('organizations');
  const [flashMessage, dismissFlashMessage] = useFlashMessage([
    UPDATE_ORG_GROUP_STORE_KEY,
    CREATE_ORG_GROUP_STORE_KEY,
    DELETE_ORG_GROUP_STORE_KEY,
  ]);
  const isSearching = useSelector(createLoadingSelector([SEARCH_ORG_GROUP_ORGANIZATIONS_STORE_KEY]));
  const isSearchingUnused = useSelector(createLoadingSelector([SEARCH_UNUSED_ORG_GROUPS_STORE_KEY]));
  const isUpdating = useSelector(createLoadingSelector([UPDATE_ORG_GROUP_STORE_KEY]));
  const searchError = useSelector(createErrorMessageSelector([SEARCH_ORG_GROUP_ORGANIZATIONS_STORE_KEY]));
  const unusedSearchError = useSelector(createErrorMessageSelector([SEARCH_UNUSED_ORG_GROUPS_STORE_KEY]));

  useEffect(() => {
    dispatch(searchOrganizationGroupOrganizationsRequested());

    return () => {
      dispatch(cancelOrganizationGroupManagementSearches());
    };
  }, [dispatch]);

  const handleQueryChange = useCallback(
    (nextQuery: string) => {
      dispatch(updateOrganizationGroupOrganizationFilters({ query: nextQuery }));
    },
    [dispatch],
  );

  const handleOnlyIncompleteChange = useCallback(
    (incomplete: boolean) => {
      dispatch(updateOrganizationGroupOrganizationFilters({ incomplete }));
    },
    [dispatch],
  );

  const navigate = useCallback(
    (target: Page) => {
      dispatch(goToOrganizationGroupOrganizationsPage(target));
    },
    [dispatch],
  );

  const updateOrganization = useCallback(
    (organizationId: string, changes: OrganizationGroupChanges) => {
      dispatch(updateOrganizationGroupRequested({ organizationId, ...changes }));
    },
    [dispatch],
  );

  const showOrganizations = useCallback(() => {
    if (activeView === 'organizations') return;

    setActiveView('organizations');
    dispatch(searchOrganizationGroupOrganizationsRequested());
  }, [activeView, dispatch]);

  const showUnusedGroups = useCallback(() => {
    if (activeView === 'unused') return;

    setActiveView('unused');
    dispatch(searchUnusedOrganizationGroupsRequested());
  }, [activeView, dispatch]);

  const navigateUnusedGroups = useCallback(
    (target: Page) => {
      dispatch(goToUnusedOrganizationGroupsPage(target));
    },
    [dispatch],
  );

  const renderResults = () => {
    if (!management.hasSearched) return <Loading />;

    if (searchError) return <PageError error={searchError} />;
    if (isSearching) return <Loading />;

    if (!management.results.length) {
      return (
        <Card>
          <Header3>No organisations found</Header3>
          <P1>
            {managementFilters.incomplete
              ? managementFilters.query.trim()
                ? 'No incomplete organisations match this search.'
                : 'All non-private organisations have both a Parent and Ultimate Parent set.'
              : 'Try another organisation, Parent or Ultimate Parent name.'}
          </P1>
        </Card>
      );
    }

    return (
      <Fragment>
        {management.results.map((organization) => (
          <RepositCard key={organization.id} title={organization.name} subtitle={organization.id}>
            <OrganizationGroupEditor
              key={`${organization.id}:${organization.organizationGroup ? organization.organizationGroup.id : ''}:${
                organization.parentOrganizationGroup ? organization.parentOrganizationGroup.id : ''
              }`}
              value={{
                organizationGroupId: organization.organizationGroup ? organization.organizationGroup.id : undefined,
                parentOrganizationGroupId: organization.parentOrganizationGroup
                  ? organization.parentOrganizationGroup.id
                  : undefined,
                organizationGroup: organization.organizationGroup,
                parentOrganizationGroup: organization.parentOrganizationGroup,
              }}
              isUpdateLoading={isUpdating}
              onUpdate={(changes) => updateOrganization(organization.id, changes)}
            />
          </RepositCard>
        ))}
        <Pagination count={managementPagination.count} currentPage={managementPagination.page} onNavigate={navigate} />
      </Fragment>
    );
  };

  const renderUnusedGroups = () => {
    if (unusedSearchError) return <PageError error={unusedSearchError} />;
    if (isSearchingUnused) return <Loading />;

    if (!unused.results.length) {
      return (
        <Card>
          <Header3>No unused entries found</Header3>
          <P1>Every Parent/Ultimate Parent is currently assigned.</P1>
        </Card>
      );
    }

    return (
      <Fragment>
        <Card flush={true}>
          <Table noBorder={true}>
            <Thead>
              <Tr>
                <Th>Name</Th>
                <Th>ID</Th>
                <Th />
              </Tr>
            </Thead>
            <Tbody>
              {unused.results.map((organizationGroup) => (
                <UnusedOrganizationGroupRow key={organizationGroup.id} organizationGroup={organizationGroup} />
              ))}
            </Tbody>
          </Table>
        </Card>
        <Pagination count={unused.count} currentPage={unused.page} onNavigate={navigateUnusedGroups} />
      </Fragment>
    );
  };

  return (
    <Container>
      <Row>
        <Col sm={12}>
          <Header2>Parent/Ultimate Parent</Header2>
          <P1>Manage Parent and Ultimate Parent assignments, and delete entries that are no longer used.</P1>
          {flashMessage ? (
            <FlashMessageContainer>
              <FlashMessage payload={flashMessage} onDismiss={dismissFlashMessage} timeRemaining={FLASH_MESSAGE_TIMEOUT} />
            </FlashMessageContainer>
          ) : undefined}
          <ViewStrip>
            <P2 style={{ margin: 0 }}>Choose what you want to manage.</P2>
            <ModeActions>
              <Button type="button" buttonType="tertiary" selected={activeView === 'organizations'} onClick={showOrganizations}>
                Organisations
              </Button>
              <Button type="button" buttonType="tertiary" selected={activeView === 'unused'} onClick={showUnusedGroups}>
                Unused entries
              </Button>
            </ModeActions>
          </ViewStrip>
          {activeView === 'organizations' ? (
            <Fragment>
              <Search
                value={managementFilters.query}
                onChange={handleQueryChange}
                placeholder="Search by organisation ID or name, Parent or Ultimate Parent"
              />
              <ModeStrip>
                <P2 style={{ margin: 0 }}>Limit results to organisations that still need a Parent or Ultimate Parent set.</P2>
                <Checkbox
                  name="onlyIncompleteOrganizations"
                  label="Only incomplete organisations"
                  isChecked={managementFilters.incomplete}
                  onChange={(event) => handleOnlyIncompleteChange(event.currentTarget.checked)}
                />
              </ModeStrip>
              <Results>{renderResults()}</Results>
            </Fragment>
          ) : (
            <Results>{renderUnusedGroups()}</Results>
          )}
        </Col>
      </Row>
    </Container>
  );
};

export default OrganizationGroups;
